import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";

type AuthCtx = {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  aal: "aal1" | "aal2";
  hasMfa: boolean;
  loading: boolean;
  refreshMfa: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [aal, setAal] = useState<"aal1" | "aal2">("aal1");
  const [hasMfa, setHasMfa] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshMfa = useCallback(async () => {
    try {
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      setAal((aalData?.currentLevel as "aal1" | "aal2") ?? "aal1");
      const { data: factors } = await supabase.auth.mfa.listFactors();
      setHasMfa(!!factors?.totp?.some((f) => f.status === "verified"));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        setTimeout(() => {
          checkAdmin(newSession.user.id);
          refreshMfa();
        }, 0);
      } else {
        setIsAdmin(false);
        setAal("aal1");
        setHasMfa(false);
      }
    });

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        checkAdmin(existing.user.id);
        refreshMfa();
      }
      setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, [refreshMfa]);

  async function checkAdmin(userId: string) {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    setIsAdmin(!!data);
  }

  const signOut: AuthCtx["signOut"] = async () => {
    await supabase.auth.signOut();
  };

  return (
    <Ctx.Provider value={{ session, user, isAdmin, aal, hasMfa, loading, refreshMfa, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
